import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { match, P } from 'ts-pattern';
import { useIxcInventoryV1SecurityDayQueryServiceSecurityDayList } from '@interxfi/api-client/generated/hooks/ixc-inventory-v1-security-day-query-service-security-day-list';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import {
  type IxcInventoryV1SecurityDayQueryServiceSecurityDayEventsSecurityDayEventsRequest,
  IxcInventoryV1SecurityDayQueryServiceSecurityDayListFilterableFields,
  IxcInventoryV1SecurityDayQueryServiceSecurityDayListSortableFields,
  type IxcInventoryV1SecurityDayState,
} from '@interxfi/types/generated/api';
import {
  InventorySecurityDayEventDrawer,
  InventorySecurityDayEventSummary,
} from '../../../../../../../../../../features/inventory/security-day/inventory-security-day-event';
import { getInventorySecurityDayStateListItems } from '../../../../../../../../../../features/inventory/security-day/inventory-security-day-list-items';
import { getInventorySecurityDayDetailPath } from '../../../../../../../../../../features/inventory/security-day/inventory-security-day-routing';
import { InventorySecurityDayStatusPill } from '../../../../../../../../../../features/inventory/security-day/inventory-security-day-status-pill';
import { InventorySecurityDaySummaryBar } from '../../../../../../../../../../features/inventory/security-day/inventory-security-day-summary-bar';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/operations/_operations/inventory/balance/_balance/$assetId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `inventory-balance-${params.assetId}`,
        label: 'Balance Detail',
        to: getInventorySecurityDayDetailPath(params.assetId),
        suffix: <CopyButton content={params.assetId} label="Copy Asset ID" tooltip={{ copy: params.assetId, copied: 'Copied Asset ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Inventory Balance'),
  component: InventorySecurityDayDetail,
});

function getListItems(data: IxcInventoryV1SecurityDayState | undefined) {
  const listItems = getInventorySecurityDayStateListItems(data);

  return {
    general: {
      left: [
        {
          id: 'main',
          items: [
            ...(listItems.data.asset.summary ? [listItems.data.asset.summary] : []),
            ...match(listItems.data.asset.data.type)
              .with({ equity: P.not(P.nullish) }, (a) => [a.equity.value.name])
              .with({ moneyMarket: P.not(P.nullish) }, (a) => [a.moneyMarket.value.name])
              .with({ cash: P.not(P.nullish) }, () => [])
              .otherwise(() => []),
          ],
        },
        {
          id: 'previousClosing',
          title: 'Previous Closing',
          items: [listItems.data.previousClosingQuantity, listItems.data.previousClosingCost],
        },
        ...(listItems.data.corporateActionAdjustments ?? []).map((adjustment, index) => ({
          id: `corporateActionAdjustment-${index}`,
          title: `Corporate Action Adjustment ${index + 1}`,
          items: [adjustment.actionId, adjustment.assignmentId, adjustment.balanceDelta, adjustment.costDelta],
        })),
        {
          id: 'opening',
          title: 'Opening',
          items: [listItems.data.openingQuantity, listItems.data.openingCost],
        },
        {
          id: 'closing',
          title: 'Closing',
          items: [listItems.data.closingQuantity, listItems.data.closingCost],
        },
        {
          id: 'netTrade',
          title: 'Net Trade',
          items: [listItems.data.netTrade.quantity, listItems.data.netTrade.value],
        },
        {
          id: 'realized',
          title: 'Realized',
          items: [
            listItems.data.realized.quantitySold,
            listItems.data.realized.cost,
            listItems.data.realized.proceeds,
            listItems.data.realized.gain,
            listItems.data.realized.transactionId,
          ],
        },
      ] satisfies ReadonlyListGroup[],
      right: [
        listItems.metadata.createdAt,
        listItems.metadata.updatedAt,
        listItems.assetId,
        listItems.tradeDate,
        listItems.settleDate,
      ] satisfies ReadonlyListItem[],
    },
  };
}

function getMenuItems(request: IxcInventoryV1SecurityDayQueryServiceSecurityDayEventsSecurityDayEventsRequest | undefined): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: InventorySecurityDayEventDrawer,
        props: { request },
      },
    },
    actions: undefined,
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load security day', 'view this security day');

function InventorySecurityDayDetail() {
  const { assetId } = Route.useParams();
  const listRequest = useMemo(
    () => ({
      query: {
        filters: [{ field: { name: IxcInventoryV1SecurityDayQueryServiceSecurityDayListFilterableFields.AssetId, type: { value: assetId } } }],
        sorts: [{ field: IxcInventoryV1SecurityDayQueryServiceSecurityDayListSortableFields.MetadataCreatedAt, descending: true }],
      },
    }),
    [assetId],
  );
  const { data, error, isPending } = useIxcInventoryV1SecurityDayQueryServiceSecurityDayList(listRequest);
  useErrorHandler(error, errorMessageGetter);
  const securityDay = data?.pages?.[0]?.securityDay?.[0];
  const showPending = Boolean(isPending || error);
  const eventsRequest = useMemo(() => (securityDay ? { assetId: securityDay.assetId, tradeDate: securityDay.tradeDate } : undefined), [securityDay]);
  const listItems = useMemo(() => getListItems(securityDay), [securityDay]);
  const menuItems = useMemo(() => getMenuItems(eventsRequest), [eventsRequest]);

  return (
    <PageLayout gap={7} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <InventorySecurityDayStatusPill isPending={showPending} size="large" status={securityDay?.status} />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <InventorySecurityDaySummaryBar isPending={showPending} securityDay={securityDay} />
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <InventorySecurityDayEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={eventsRequest} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>General</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.general.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
